import { DefaultButton, IButtonStyles, IPanelStyles, IStackStyles, Label, mergeStyles, Panel, PanelType, ScrollablePane, ScrollbarVisibility, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/components/_v2/redux/storeMain";
import { setContentManagerFilterSections, setContentManagerFilterSectionsReset } from "../../../src/taskpane/redux/modules/contentManagerDirectory";
import { toggleReload } from "../../../src/taskpane/redux/modules/reload";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { CMFileFilterPanelSection } from "./CMFileFilterPanelSection";
import Loader from "../../../src/taskpane/components/Loader";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { setFilterSectionsFromFields } from "./FileFilterHelper";

export const scrollStyle = mergeStyles({
  position: "relative",
  height: "calc(100vh - 140px)",
  marginTop: 5,
  marginBottom: 10
});

export const panelActionButtonStyle: Partial<IButtonStyles> = {
  root: {
    marginTop: 20,
  }
}

const panelStyles: Partial<IPanelStyles> = {
  main: {
    overflow: "hidden"
  },
  content: {
    paddingLeft: 5,
    paddingRight: 5,
    paddingBottom: 0
  }
}

const noFiltersStackStyles: Partial<IStackStyles> = {
  root: { width: "100%", height: "100%", margin: 0, padding: 0, overflow: "auto" }
};

const stackTokens = { childrenGap: 25 };

export interface ICMFileFilterPanelProps {
  closePanel?: () => void;
  panelIsOpen: boolean;
  isInOneDrive: boolean;
  serverRelPath: string;
  selectedView?: IViewsDropdown;
}

export const CMFileFilterPanel = ({ closePanel, panelIsOpen, isInOneDrive, serverRelPath, selectedView }: ICMFileFilterPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const filterFields = useSelector((state: RootState) => state.contentManagerDirectory.filterFields);
  const filterSections = useSelector((state: RootState) => state.contentManagerDirectory.filterSections);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const userAccount = useSelector((state: RootState) => state.user.account);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const [isLoadingFilters, setIsLoadingFilters] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const [filterSelected, setFilterSelected] = React.useState<boolean>(false);
  const [hasFilters, setHasFilters] = React.useState(true);

  React.useEffect(() => {
    setIsLoadingFilters(true);
    if (filterSections.length === 0) {
      (async () => {
        try {
          const displayFilterSections = await setFilterSectionsFromFields(isInOneDrive,
            oneDriveRootDetails,
            currentDirectory,
            userAccount,
            filterFields,
            serverRelPath,
            languageStrings.Me,
            selectedView,
            datasourceItems
          );

          dispatch(setContentManagerFilterSections(displayFilterSections));
          setHasFilters(displayFilterSections.some(section => section.items?.length > 0));
        } catch (error) {
          // Ignore
        }
        setIsLoadingFilters(false);
      })();
    }
    else {
      setIsLoadingFilters(false);
    }
  }, []);

  React.useEffect(() => {
    setFilterSelected(hasFilters && (filterSections?.some(section => section.daysOffset || section.daysOffset === 0) || filterSections?.some(section => section.items.some(item => item.selected === true))));
  }, [hasFilters, filterSections]);

  return (
    <Panel
      headerText={languageStrings.Filter}
      isOpen={panelIsOpen}
      onDismiss={() => {
        closePanel();
      }}
      isLightDismiss={true}
      closeButtonAriaLabel="Close"
      type={PanelType.custom}
      customWidth={'350px'}
      styles={panelStyles}
    >
      <div className={scrollStyle} >
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          <div className="xb-content" style={{ marginLeft: 20 }}>
            {isLoadingFilters && <Loader label={languageStrings.LoadingFilters} />}
            {!hasFilters && <Stack verticalAlign="center" horizontalAlign="center" styles={noFiltersStackStyles}>
              <Stack.Item align="center">
                <Label disabled>{languageStrings.NoFiltersAvailable}</Label>
              </Stack.Item>
            </Stack>}
            {hasFilters && <Stack tokens={stackTokens}>
              {filterSections?.length > 0
                && filterSections
                  .map((section, index) => {
                    return (
                      <Stack.Item key={index}>
                        <CMFileFilterPanelSection
                          filterSections={filterSections}
                          filterSection={section}
                          isInOneDrive={isInOneDrive}
                          serverRelPath={serverRelPath}
                          oneDriveRootDetails={oneDriveRootDetails}
                          currentDirectory={currentDirectory}
                          isInContentManagerMode={true}
                        />
                      </Stack.Item>
                    )
                  })}
            </Stack>}
          </div>
        </ScrollablePane>
      </div>
      {filterSections?.length > 0 && hasFilters && <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 15 }} style={{ paddingRight: 10 }}>
        <DefaultButton
          styles={panelActionButtonStyle}
          iconProps={{ iconName: 'Refresh' }}
          disabled={!filterSelected}
          onClick={async () => {
            dispatch(setContentManagerFilterSectionsReset());
            dispatch(toggleReload());
          }}
          text={languageStrings.Reset}
        />
      </Stack>}
    </Panel>
  )
};